import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, doc as firestoreDoc, getDoc, getDocs, query, where } from "firebase/firestore";
import * as XLSX from "xlsx";
import db from "../Firebase";

const activityLabels = ["Grow", "Khata", "Appointments", "Portfolio"];

const periodConfig = {
    before: { label: "Before 1 Feb 2025", description: "Data before 01 Feb 2025" },
    after:  { label: "After 1 Feb 2025",  description: "Data from 01 Feb 2025 onwards" },
};

const cutoffDate = new Date("2025-02-01T00:00:00");

const parseDateValue = (val) => {
    if (!val) return null;
    if (typeof val?.toDate === "function") return val.toDate();
    if (val instanceof Date) return val;
    if (typeof val?.seconds === "number") return new Date(val.seconds * 1000);
    const d = new Date(val);
    return isNaN(d) ? null : d;
};

const isInPeriod = (val, period) => {
    const d = parseDateValue(val);
    if (!d) return false;
    return period === "before" ? d < cutoffDate : d >= cutoffDate;
};

const fmtDate = (val) => {
    const d = parseDateValue(val);
    if (!d) return val ? String(val) : "-";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const toTitleCase = (str) => {
    if (!str) return "-";
    return String(str).toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
};

const cleanText = (val, fallback = "-") => {
    if (val === null || val === undefined || val === "") return fallback;
    return String(val);
};

const compactPhone = (phone) => String(phone || "").replace(/\D/g, "").slice(-10);

const createEmptyActivityCounts = () => activityLabels.reduce((acc, label) => {
    acc[label] = 0;
    return acc;
}, {});

const matchesText = (visit, words) => {
    const text = [visit.page_name, visit.category, visit.description]
        .map((v) => cleanText(v, "").toLowerCase()).join(" ");
    return words.some((word) => text.includes(word));
};

const activityTypeForVisit = (visit = {}) => {
    if (matchesText(visit, ["portfolio"])) return "Portfolio";
    if (matchesText(visit, ["grow"])) return "Grow";
    return null;
};

// Run at most `concurrency` promises at a time
const throttledAll = async (tasks, concurrency = 25) => {
    const results = [];
    for (let i = 0; i < tasks.length; i += concurrency) {
        const batch = await Promise.all(tasks.slice(i, i + concurrency).map(t => t()));
        results.push(...batch);
    }
    return results;
};

const batchedIn = async (collName, field, values) => {
    const cleanValues = [...new Set(values.filter(Boolean))];
    if (cleanValues.length === 0) return [];
    const chunks = [];
    for (let i = 0; i < cleanValues.length; i += 10) chunks.push(cleanValues.slice(i, i + 10));
    // Process max 25 Firestore `in` queries at a time
    const docs = await throttledAll(
        chunks.map(chunk => () => getDocs(query(collection(db, collName), where(field, "in", chunk)))),
        25
    );
    return docs.flatMap(snap => snap.docs);
};

const transactionDate = (data) => data.created_date || data.date;

// Determine group from funder field value (partial, case-insensitive)
const determineGroup = (funder) => {
    const f = (funder || "").toLowerCase().trim();
    if (f.includes("abc")) return "ABC";
    if (f.includes("nascom") || f.includes("nasscom")) return "nascom";
    return "Others"; // any other funder → Others bucket
};

const GROUP_COLORS = { ABC: "#4361ee", nascom: "#7209b7", Others: "#0d9488" };

function PrePostActivity() {
    const [rows, setRows]               = useState([]);
    const [loading, setLoading]         = useState(true);
    const [groupFilter, setGroupFilter] = useState("all");
    const [activePeriod, setActivePeriod]     = useState("before");
    const [portfolioModal, setPortfolioModal] = useState(null);
    const [activityModal, setActivityModal]   = useState(null);

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                // Fetch both collections in parallel
                const [funderSnap, personalSnap] = await Promise.all([
                    getDocs(collection(db, "funder_users_list")),
                    getDocs(collection(db, "personal_details")),
                ]);

                const rowMap = {};

                // ── Build ABC / nascom rows from funder_users_list ──────────
                const funderPhones = new Set();
                funderSnap.docs.forEach((docSnap) => {
                    const d = docSnap.data();
                    const phone = compactPhone(d.phone_no || d.phone || docSnap.id);
                    if (!phone) return;
                    funderPhones.add(phone); // always mark as funder phone
                    const group = determineGroup(d.funder);
                    if (rowMap[phone]) return;

                    rowMap[phone] = {
                        id: docSnap.id,
                        name: cleanText(d.name),
                        phone,
                        username: cleanText(d.username),
                        funder: cleanText(d.funder),
                        phase: cleanText(d.phase),
                        group,
                        ageRange: d.age_range || null,
                        profileType: d.profile_type || null,
                        city: d.city || null,
                        state: d.state || null,
                        before: { portfolioCreated: false, portfolioUpdated: false, portfolioDetails: [], activityCounts: createEmptyActivityCounts(), activityDetails: [], khataDetails: [], appointmentDetails: [] },
                        after:  { portfolioCreated: false, portfolioUpdated: false, portfolioDetails: [], activityCounts: createEmptyActivityCounts(), activityDetails: [], khataDetails: [], appointmentDetails: [] },
                    };
                });

                // ── Build Others rows from personal_details ─────────────────
                personalSnap.docs.forEach((docSnap) => {
                    const d = docSnap.data();
                    // personal_details uses phone_no field; doc ID is a random UID — don't use it as phone
                    const phone = compactPhone(d.phone_no || d.phone);
                    if (!phone || phone.length < 10 || funderPhones.has(phone) || rowMap[phone]) return;

                    rowMap[phone] = {
                        id: docSnap.id,
                        name: cleanText(d.full_name || d.name),
                        phone,
                        username: cleanText(d.username),
                        funder: "-",
                        phase: "-",
                        group: "Others",
                        ageRange: d.age_range || null,
                        profileType: d.type || d.profile_type || null,
                        city: null,
                        state: null,
                        before: { portfolioCreated: false, portfolioUpdated: false, portfolioDetails: [], activityCounts: createEmptyActivityCounts(), activityDetails: [], khataDetails: [], appointmentDetails: [] },
                        after:  { portfolioCreated: false, portfolioUpdated: false, portfolioDetails: [], activityCounts: createEmptyActivityCounts(), activityDetails: [], khataDetails: [], appointmentDetails: [] },
                    };
                });

                const phones = Object.keys(rowMap);

                // Split phones: funder users (ABC/nascom) get full enrichment incl. activity
                // Others only get portfolio + khata to avoid 50k+ individual getDoc calls
                const funderPhoneList = phones.filter(p => rowMap[p].group !== "Others");
                const otherPhoneList  = phones.filter(p => rowMap[p].group === "Others");

                const usernameToPhone = {};
                Object.values(rowMap).forEach((row) => {
                    if (row.username && row.username !== "-") usernameToPhone[row.username] = row.phone;
                });
                // For khata, only look up funder usernames (Others rarely have khata data)
                const funderUsernames = funderPhoneList
                    .map(p => rowMap[p].username)
                    .filter(u => u && u !== "-");

                // Enrich profile type & city/state — funder users only (Others have it from personal_details)
                const ptDocs = await batchedIn("profile_type", "phone_no", funderPhoneList);
                ptDocs.forEach((docSnap) => {
                    const d = docSnap.data();
                    const phone = compactPhone(d.phone_no);
                    if (!rowMap[phone]) return;
                    rowMap[phone].ageRange    = rowMap[phone].ageRange    || d.age_range    || null;
                    rowMap[phone].profileType = rowMap[phone].profileType || d.profile_type || null;
                });

                const pnDocs = await batchedIn("parlour_name", "phone", funderPhoneList);
                pnDocs.forEach((docSnap) => {
                    const d = docSnap.data();
                    const phone = compactPhone(d.phone);
                    if (!rowMap[phone]) return;
                    rowMap[phone].city  = rowMap[phone].city  || d.city  || null;
                    rowMap[phone].state = rowMap[phone].state || d.state || null;
                });

                // Portfolio data — ALL users (batched efficiently via `in` queries)
                const pfDocs = await batchedIn("nv_portfolio_details", "phone_no", phones);
                pfDocs.forEach((docSnap) => {
                    const d = docSnap.data();
                    const phone = compactPhone(d.phone_no);
                    if (!rowMap[phone]) return;
                    ["before", "after"].forEach((period) => {
                        const createdInPeriod = isInPeriod(d.created_date, period);
                        const updatedInPeriod = isInPeriod(d.updated_date, period);
                        if (!createdInPeriod && !updatedInPeriod) return;
                        rowMap[phone][period].portfolioCreated = rowMap[phone][period].portfolioCreated || createdInPeriod;
                        rowMap[phone][period].portfolioUpdated = rowMap[phone][period].portfolioUpdated || updatedInPeriod;
                        rowMap[phone][period].portfolioDetails.push({
                            phone_no: phone,
                            profession: d.profession || "-",
                            created_date: d.created_date,
                            updated_date: d.updated_date,
                            status: [createdInPeriod ? "Created" : null, updatedInPeriod ? "Updated" : null].filter(Boolean).join(" + "),
                        });
                    });
                });

                // Khata data — funder users only
                const khataDocs = await Promise.all([
                    batchedIn("nv_income",  "username", funderUsernames),
                    batchedIn("nv_expense", "username", funderUsernames),
                ]);
                khataDocs.flat().forEach((docSnap) => {
                    const d = docSnap.data();
                    const phone = usernameToPhone[cleanText(d.username, "")];
                    if (!phone || !rowMap[phone]) return;
                    ["before", "after"].forEach((period) => {
                        if (!isInPeriod(transactionDate(d), period)) return;
                        rowMap[phone][period].activityCounts.Khata += 1;
                        rowMap[phone][period].khataDetails.push({
                            type: d.type || "-", username: d.username,
                            category: d.category || "-", subcategory: d.subcategory || "-",
                            charges: d.charges || "-", payment_method: d.payment_method || "-",
                            date: d.date || null, created_date: d.created_date || null,
                        });
                    });
                });

                // Appointments — funder users only (joined by username)
                const apptDocs = await batchedIn("nv_create_booking", "username", funderUsernames);
                apptDocs.forEach((docSnap) => {
                    const d = docSnap.data();
                    const phone = usernameToPhone[cleanText(d.username, "")];
                    if (!phone || !rowMap[phone]) return;
                    ["before", "after"].forEach((period) => {
                        if (!isInPeriod(d.created_date, period)) return;
                        rowMap[phone][period].activityCounts.Appointments += 1;
                        rowMap[phone][period].appointmentDetails.push({
                            username: d.username,
                            service: d.service_name || d.service || d.type || "-",
                            status: d.status || d.booking_status || "-",
                            booking_date: d.booking_date || d.appointment_date || null,
                            created_date: d.created_date,
                        });
                    });
                });

                // Activity (Grow/Portfolio visits) — funder users ONLY, throttled 25 at a time
                const activityDocs = await throttledAll(
                    funderPhoneList.map(phone => async () => {
                        const snap = await getDoc(firestoreDoc(db, "users_activity", phone));
                        return snap.exists() ? { phone, data: snap.data() } : null;
                    }),
                    25
                );
                activityDocs.filter(Boolean).forEach(({ phone, data }) => {
                    if (!rowMap[phone]) return;
                    const visits = Array.isArray(data.page_visits) ? data.page_visits : [];
                    visits.forEach((visit) => {
                        const type = activityTypeForVisit(visit);
                        if (!type) return;
                        ["before", "after"].forEach((period) => {
                            if (!isInPeriod(visit.visit_time, period)) return;
                            rowMap[phone][period].activityCounts[type] += 1;
                            rowMap[phone][period].activityDetails.push({
                                type, phone_no: phone,
                                category: cleanText(visit.category),
                                page_name: cleanText(visit.page_name),
                                description: cleanText(visit.description),
                                visit_time: visit.visit_time,
                            });
                        });
                    });
                });

                // Sort details
                Object.values(rowMap).forEach((row) => {
                    ["before", "after"].forEach((period) => {
                        row[period].activityDetails.sort((a, b) =>
                            (parseDateValue(b.visit_time)?.getTime() || 0) - (parseDateValue(a.visit_time)?.getTime() || 0));
                        row[period].khataDetails.sort((a, b) =>
                            (parseDateValue(transactionDate(b))?.getTime() || 0) - (parseDateValue(transactionDate(a))?.getTime() || 0));
                    });
                });

                setRows(Object.values(rowMap).sort((a, b) => cleanText(a.name).localeCompare(cleanText(b.name))));
            } catch (err) {
                console.error("PrePostActivity fetch error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, []);

    const filteredRows = useMemo(
        () => rows.filter((row) => groupFilter === "all" || row.group === groupFilter),
        [rows, groupFilter]
    );

    // ── Summary counts for both periods ──────────────────────────────────────
    const periodTotals = useMemo(() => {
        const build = (period) => filteredRows.reduce((acc, row) => {
            const d = row[period];
            acc.portfolioCreated += d.portfolioCreated ? 1 : 0;
            acc.portfolioUpdated += d.portfolioUpdated ? 1 : 0;
            acc.grow             += d.activityCounts.Grow;
            acc.khata            += d.activityCounts.Khata;
            return acc;
        }, { portfolioCreated: 0, portfolioUpdated: 0, grow: 0, khata: 0 });
        return { before: build("before"), after: build("after") };
    }, [filteredRows]);

    const handleExport = () => {
        const data = filteredRows.map((row, i) => {
            const d = row[activePeriod];
            return {
                "#": i + 1, Name: toTitleCase(row.name), Phone: row.phone,
                Username: row.username, Group: row.group, Funder: row.funder, Phase: row.phase,
                City: row.city || "-", State: row.state || "-",
                "Age Range": row.ageRange || "-", "Profile Type": row.profileType || "-",
                "Portfolio Created": d.portfolioCreated ? "Yes" : "No",
                "Portfolio Updated": d.portfolioUpdated ? "Yes" : "No",
                "Grow Usage": d.activityCounts.Grow,
                "Khata Usage": d.activityCounts.Khata,
                "Appointments": d.activityCounts.Appointments,
                "Portfolio Usage": d.activityCounts.Portfolio,
            };
        });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), "Summary");
        XLSX.writeFile(wb, `pre_post_activity_${activePeriod}.xlsx`);
    };

    const groupCounts = useMemo(() => ({
        all:    rows.length,
        ABC:    rows.filter(r => r.group === "ABC").length,
        nascom: rows.filter(r => r.group === "nascom").length,
        Others: rows.filter(r => r.group === "Others").length,
    }), [rows]);

    const cardMetrics = [
        { key: "portfolioCreated", label: "Portfolio Created", icon: "🖼️", color: "#4361ee" },
        { key: "grow",             label: "Grow Usage",        icon: "📈", color: "#0d9488" },
        { key: "khata",            label: "Khata Usage",       icon: "📒", color: "#7209b7" },
        { key: "portfolioUpdated", label: "Portfolio Updated", icon: "✏️", color: "#f3722c" },
    ];

    return (
        <>
            <div className="container-fluid py-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h3 className="mb-1">Pre Post Activity</h3>
                        <div className="text-muted" style={{ fontSize: "13px" }}>
                            Compare users before and after 01 Feb 2025 · ABC · nascom · Others
                        </div>
                    </div>
                    <Link to="/" className="btn btn-outline-secondary btn-sm">Back</Link>
                </div>

                {/* GROUP FILTER */}
                <div className="card mb-3">
                    <div className="card-body py-3 px-4">
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            <span style={{ fontSize: "12px", fontWeight: 700, color: "#5f6773", textTransform: "uppercase", marginRight: "4px" }}>Group</span>
                            {[
                                { val: "all",    label: `All (${groupCounts.all})`,       color: "#475569" },
                                { val: "ABC",    label: `ABC (${groupCounts.ABC})`,       color: GROUP_COLORS.ABC },
                                { val: "nascom", label: `nascom (${groupCounts.nascom})`, color: GROUP_COLORS.nascom },
                                { val: "Others", label: `Others (${groupCounts.Others})`, color: GROUP_COLORS.Others },
                            ].map(({ val, label, color }) => (
                                <button
                                    key={val}
                                    onClick={() => setGroupFilter(val)}
                                    style={{
                                        padding: "6px 18px", borderRadius: "20px", fontWeight: 700, fontSize: "13px",
                                        border: `2px solid ${color}`, cursor: "pointer", transition: "all 0.15s",
                                        background: groupFilter === val ? color : "#fff",
                                        color: groupFilter === val ? "#fff" : color,
                                    }}
                                >
                                    {label}
                                </button>
                            ))}
                            <button
                                className="btn btn-success ms-auto"
                                style={{ height: "38px", minWidth: "130px", borderRadius: "8px", fontWeight: 700 }}
                                onClick={handleExport}
                                disabled={loading || filteredRows.length === 0}
                            >
                                Export Excel
                            </button>
                        </div>
                    </div>
                </div>

                {/* SUMMARY CARDS — Before vs After */}
                {!loading && (
                    <div className="row g-3 mb-3">
                        {cardMetrics.map(({ key, label, icon, color }) => (
                            <div className="col-md-6 col-lg-3" key={key}>
                                <div className="card border-0 shadow-sm" style={{ borderRadius: "14px", overflow: "hidden" }}>
                                    <div style={{ background: color, padding: "12px 18px" }}>
                                        <div style={{ color: "rgba(255,255,255,0.85)", fontSize: "12px", fontWeight: 700 }}>{icon} {label}</div>
                                    </div>
                                    <div className="card-body py-3 px-4">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className="text-center flex-fill" style={{ borderRight: "1px solid #e2e8f0" }}>
                                                <div style={{ fontSize: "1.8rem", fontWeight: 800, color, lineHeight: 1 }}>
                                                    {periodTotals.before[key]}
                                                </div>
                                                <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "4px" }}>Before 1 Feb</div>
                                            </div>
                                            <div className="text-center flex-fill">
                                                <div style={{ fontSize: "1.8rem", fontWeight: 800, color, lineHeight: 1 }}>
                                                    {periodTotals.after[key]}
                                                </div>
                                                <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "4px" }}>After 1 Feb</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {loading ? (
                    <div className="text-center py-5">Loading…</div>
                ) : (
                    <div className="card mb-4">
                        <div className="card-header bg-white">
                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                                <div className="btn-group" role="group">
                                    {["before", "after"].map((period) => (
                                        <button
                                            key={period}
                                            type="button"
                                            className={`btn ${activePeriod === period ? "btn-primary" : "btn-outline-primary"}`}
                                            onClick={() => setActivePeriod(period)}
                                            style={{ minWidth: "180px", fontWeight: 700 }}
                                        >
                                            {periodConfig[period].label}
                                        </button>
                                    ))}
                                </div>
                                <span className="badge bg-secondary" style={{ fontSize: "13px", padding: "9px 11px" }}>
                                    {periodConfig[activePeriod].description} · {filteredRows.length} users
                                </span>
                            </div>
                        </div>

                        <div className="table-responsive">
                            <table className="table table-bordered table-hover align-middle mb-0" style={{ fontSize: "13px" }}>
                                <thead className="table-dark">
                                    <tr>
                                        <th>#</th>
                                        <th>Group</th>
                                        <th>Name</th>
                                        <th>Phone</th>
                                        <th>Username</th>
                                        <th>Funder</th>
                                        <th>Phase</th>
                                        <th>City</th>
                                        <th>State</th>
                                        <th>Profile Type</th>
                                        <th className="text-center">Portfolio</th>
                                        <th className="text-center">Grow Usage</th>
                                        <th className="text-center">Khata Usage</th>
                                        <th className="text-center">Portfolio Usage</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRows.length === 0 ? (
                                        <tr><td colSpan="14" className="text-center text-muted py-4">No data found</td></tr>
                                    ) : filteredRows.map((row, index) => {
                                        const data = row[activePeriod];
                                        const gColor = GROUP_COLORS[row.group] || "#475569";
                                        return (
                                            <tr key={row.phone}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    <span style={{ background: gColor, color: "#fff", padding: "2px 10px", borderRadius: "12px", fontSize: "11px", fontWeight: 700 }}>
                                                        {row.group}
                                                    </span>
                                                </td>
                                                <td>{toTitleCase(row.name)}</td>
                                                <td>{row.phone}</td>
                                                <td>{row.username}</td>
                                                <td>{row.funder}</td>
                                                <td>{row.phase}</td>
                                                <td>{row.city || "-"}</td>
                                                <td>{row.state || "-"}</td>
                                                <td>{row.profileType || "-"}</td>
                                                <td className="text-center">
                                                    {data.portfolioDetails.length > 0 ? (
                                                        <span
                                                            className={data.portfolioUpdated ? "badge bg-success" : "badge bg-primary"}
                                                            style={{ cursor: "pointer" }}
                                                            onClick={() => setPortfolioModal({ phone: row.phone, items: data.portfolioDetails })}
                                                        >
                                                            {data.portfolioUpdated ? "Updated" : "Created"}
                                                        </span>
                                                    ) : <span className="text-muted">-</span>}
                                                </td>
                                                {activityLabels.map((label) => (
                                                    <td key={label} className="text-center">
                                                        {data.activityCounts[label] > 0 ? (
                                                            <span
                                                                className="badge bg-dark"
                                                                style={{ cursor: "pointer" }}
                                                                onClick={() => setActivityModal({
                                                                    phone: row.phone,
                                                                    title: `${label} Usage`,
                                                                    items: label === "Khata"
                                                                        ? data.khataDetails
                                                                        : label === "Appointments"
                                                                            ? data.appointmentDetails
                                                                            : data.activityDetails.filter((item) => item.type === label),
                                                                    mode: label === "Khata" ? "khata" : label === "Appointments" ? "appointments" : "activity",
                                                                })}
                                                            >
                                                                {data.activityCounts[label]}
                                                            </span>
                                                        ) : <span className="text-muted">-</span>}
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {portfolioModal && (
                <DetailModal title="Portfolio Usage" phone={portfolioModal.phone} onClose={() => setPortfolioModal(null)}>
                    <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: "13px" }}>
                        <thead className="table-dark"><tr><th>#</th><th>Phone</th><th>Profession</th><th>Status</th><th>Created</th><th>Updated</th></tr></thead>
                        <tbody>
                            {portfolioModal.items.map((item, i) => (
                                <tr key={i}>
                                    <td>{i + 1}</td>
                                    <td>{item.phone_no}</td>
                                    <td>{item.profession}</td>
                                    <td>{item.status}</td>
                                    <td>{fmtDate(item.created_date)}</td>
                                    <td>{fmtDate(item.updated_date)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </DetailModal>
            )}

            {activityModal && (
                <DetailModal title={activityModal.title} phone={activityModal.phone} onClose={() => setActivityModal(null)} maxWidth="760px">
                    {activityModal.mode === "khata" ? (
                        <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: "13px" }}>
                            <thead className="table-dark"><tr><th>#</th><th>Type</th><th>Username</th><th>Category</th><th>Subcategory</th><th>Charges</th><th>Payment</th><th>Date</th><th>Created</th></tr></thead>
                            <tbody>
                                {activityModal.items.map((item, i) => (
                                    <tr key={i}>
                                        <td>{i + 1}</td>
                                        <td><span className={item.type === "income" ? "badge bg-success" : "badge bg-danger"}>{item.type}</span></td>
                                        <td>{item.username}</td>
                                        <td>{item.category}</td>
                                        <td>{item.subcategory}</td>
                                        <td>{item.charges}</td>
                                        <td>{item.payment_method}</td>
                                        <td>{fmtDate(item.date)}</td>
                                        <td>{fmtDate(item.created_date)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : activityModal.mode === "appointments" ? (
                        <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: "13px" }}>
                            <thead className="table-dark"><tr><th>#</th><th>Username</th><th>Service</th><th>Status</th><th>Booking Date</th><th>Created</th></tr></thead>
                            <tbody>
                                {activityModal.items.map((item, i) => (
                                    <tr key={i}>
                                        <td>{i + 1}</td>
                                        <td>{item.username}</td>
                                        <td>{item.service}</td>
                                        <td>{item.status}</td>
                                        <td>{fmtDate(item.booking_date)}</td>
                                        <td>{fmtDate(item.created_date)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: "13px" }}>
                            <thead className="table-dark"><tr><th>#</th><th>Type</th><th>Category</th><th>Page</th><th>Description</th><th>Visit Time</th></tr></thead>
                            <tbody>
                                {activityModal.items.map((item, i) => (
                                    <tr key={i}>
                                        <td>{i + 1}</td>
                                        <td><span className="badge bg-primary">{item.type}</span></td>
                                        <td>{item.category}</td>
                                        <td>{item.page_name}</td>
                                        <td>{item.description}</td>
                                        <td>{fmtDate(item.visit_time)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </DetailModal>
            )}
        </>
    );
}

function DetailModal({ title, phone, onClose, children, maxWidth = "560px" }) {
    return (
        <div
            onClick={onClose}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{ background: "#fff", borderRadius: "8px", width: "100%", maxWidth, maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 40px rgba(0,0,0,0.25)" }}
            >
                <div className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom">
                    <div>
                        <div className="fw-bold" style={{ fontSize: "15px" }}>{title}</div>
                        <div className="text-muted" style={{ fontSize: "12px" }}>{phone}</div>
                    </div>
                    <button className="btn-close" onClick={onClose} />
                </div>
                <div style={{ overflowY: "auto", padding: "16px" }}>{children}</div>
            </div>
        </div>
    );
}

export default PrePostActivity;
